import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { db } from '../../firebase-config';
import { doc, getDoc, setDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import {
  NOTIFICATION_MAX_IN_MEMORY,
  NOTIFICATION_MAX_GEOFENCE_IN_UI,
  NOTIFICATION_MAX_OFFLINE_IN_UI,
  NOTIFICATION_MAX_SOS_IN_UI,
  buildNotificationListForUi,
  isGeofenceCloudNotification,
  isOfflineCloudNotification,
  isSosCloudNotification,
  sortNotificationsByTimestampDesc,
} from '../../constants/notificationConfig';

const NOTIFICATION_STATE_COLLECTION = 'notification_state';

/**
 * LG Band SOS entries: persisted in `notifications` (same org feed as geofence entry/exit, so they
 * reach every operator and survive reloads). The client that first sees the SOS also adds the row
 * locally (addNotification) so the bell updates before the Firestore round-trip.
 */
export const isSosNotification = isSosCloudNotification;

const getUserStateRef = (userId) =>
  doc(db, NOTIFICATION_STATE_COLLECTION, userId);

/**
 * Load per-user read/dismissed state from Firestore.
 */
export const loadUserNotificationState = createAsyncThunk(
  'notifications/loadUserState',
  async (userId, { rejectWithValue }) => {
    try {
      if (!userId) return { readIds: [], dismissedIds: [] };
      const snap = await getDoc(getUserStateRef(userId));
      if (snap.exists()) {
        const data = snap.data();
        return {
          readIds: data.readIds || [],
          dismissedIds: data.dismissedIds || [],
        };
      }
      return { readIds: [], dismissedIds: [] };
    } catch (error) {
      console.error('Error loading notification state:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Mark all currently-unread notifications as read for this user.
 * Writes to Firestore so the state persists across sessions and devices.
 */
export const markNotificationsAsRead = createAsyncThunk(
  'notifications/markAsRead',
  async (userId, { getState, rejectWithValue }) => {
    try {
      if (!userId) return 0;
      const { notifications } = getState().notifications;
      const unreadIds = notifications.filter(n => !n.isRead).map(n => n.id);
      if (unreadIds.length === 0) return 0;

      const ref = getUserStateRef(userId);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        await updateDoc(ref, { readIds: arrayUnion(...unreadIds) });
      } else {
        await setDoc(ref, { readIds: unreadIds, dismissedIds: [] });
      }
      return unreadIds.length;
    } catch (error) {
      console.error('Error marking notifications as read:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Dismiss (clear) all visible notifications for this user.
 */
export const clearAllNotifications = createAsyncThunk(
  'notifications/clearAll',
  async (userId, { getState, rejectWithValue }) => {
    try {
      if (!userId) return 0;
      const { notifications } = getState().notifications;
      const ids = notifications.map(n => n.id);
      if (ids.length === 0) return 0;

      const ref = getUserStateRef(userId);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        await updateDoc(ref, { dismissedIds: arrayUnion(...ids) });
      } else {
        await setDoc(ref, { readIds: [], dismissedIds: ids });
      }
      return ids.length;
    } catch (error) {
      console.error('Error clearing notifications:', error);
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  notifications: [],
  unreadCount: 0,
  loading: false,
  error: null,
  lastUpdate: null,
  userReadIds: [],
  userDismissedIds: [],
};

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    /**
     * Receive raw notifications from the Firestore listener.
     * Apply the per-user read/dismissed state that is already in Redux.
     */
    updateNotifications: (state, action) => {
      const dismissed = new Set(state.userDismissedIds);
      const read = new Set(state.userReadIds);
      const previousById = new Map(state.notifications.map((n) => [n.id, n]));

      const capped = buildNotificationListForUi(action.payload);
      const filtered = capped
        .filter((n) => !dismissed.has(n.id))
        .map((n) => {
          const prev = previousById.get(n.id);
          const isOffline = isOfflineCloudNotification(n);

          if (isOffline && prev) {
            return {
              ...n,
              offlineMins: n.offlineMins ?? prev.offlineMins,
              timestamp: prev.timestamp ?? n.timestamp,
              isRead: prev.isRead || read.has(n.id),
            };
          }

          return {
            ...n,
            isRead: read.has(n.id),
          };
        });

      // SOS: Firestore rows (in `filtered`) are authoritative. A locally-added SOS row (announced by
      // this client before its document arrived, or not yet persisted) is kept until a Firestore
      // row with the same id replaces it.
      const cloudSosIds = new Set(filtered.filter(isSosCloudNotification).map((n) => n.id));
      const sosLocal = state.notifications
        .filter(isSosCloudNotification)
        .filter((n) => !cloudSosIds.has(n.id) && !dismissed.has(n.id))
        .map((n) => ({ ...n, isRead: n.isRead || read.has(n.id) }));
      const sosMerged = sortNotificationsByTimestampDesc([
        ...filtered.filter(isSosCloudNotification).map((n) => ({ ...n, isRead: n.isRead || previousById.get(n.id)?.isRead || false })),
        ...sosLocal,
      ]).slice(0, NOTIFICATION_MAX_SOS_IN_UI);
      const nonSos = filtered.filter((n) => !isSosCloudNotification(n));
      state.notifications = [...sosMerged, ...nonSos];
      state.unreadCount = nonSos.filter((n) => {
        if (n.isRead) return false;
        const prev = previousById.get(n.id);
        if (isOfflineCloudNotification(n) && prev) return false;
        return true;
      }).length + sosMerged.filter((n) => !n.isRead).length;
      state.lastUpdate = new Date().toISOString();
    },

    /**
     * Update the per-user read/dismissed arrays (called when the
     * notification_state/{userId} listener fires).
     */
    setUserNotificationState: (state, action) => {
      const { readIds = [], dismissedIds = [] } = action.payload;
      state.userReadIds = readIds;
      state.userDismissedIds = dismissedIds;

      const dismissed = new Set(dismissedIds);
      const read = new Set(readIds);

      state.notifications = state.notifications
        .filter(n => !dismissed.has(n.id))
        .map(n => ({
          ...n,
          isRead: read.has(n.id),
        }));
      state.unreadCount = state.notifications.filter(n => !n.isRead).length;
    },

    addNotification: (state, action) => {
      const dismissed = new Set(state.userDismissedIds);
      const read = new Set(state.userReadIds);

      const newNotification = {
        id: action.payload.id || Date.now().toString(),
        ...action.payload,
        timestamp: action.payload.timestamp || new Date().toISOString(),
        isRead: false,
      };

      if (dismissed.has(newNotification.id)) return;
      if (read.has(newNotification.id)) newNotification.isRead = true;

      if (isSosNotification(newNotification)) {
        const others = state.notifications.filter((n) => !isSosNotification(n));
        let sos = state.notifications.filter(isSosNotification).filter((n) => n.id !== newNotification.id);
        sos.unshift(newNotification);
        sos = sortNotificationsByTimestampDesc(sos).slice(0, NOTIFICATION_MAX_SOS_IN_UI);
        state.notifications = [...sos, ...others];
        state.unreadCount = state.notifications.filter((n) => !n.isRead).length;
        return;
      }

      // Upsert: if same ID already exists (offline re-check), update offlineMins in-place
      const isOffline = isOfflineCloudNotification(newNotification);
      const isGeofence = isGeofenceCloudNotification(newNotification);

      let geofence = state.notifications.filter(isGeofenceCloudNotification);
      let offline = state.notifications.filter(isOfflineCloudNotification);

      if (isOffline) {
        const idx = offline.findIndex((n) => n.id === newNotification.id);
        if (idx >= 0) {
          const existing = offline[idx];
          existing.offlineMins = newNotification.offlineMins ?? existing.offlineMins;
        } else {
          if (read.has(newNotification.id)) newNotification.isRead = true;
          else newNotification.isRead = false;
          offline.unshift(newNotification);
        }
      } else if (isGeofence) {
        const idx = geofence.findIndex((n) => n.id === newNotification.id);
        if (idx >= 0) geofence[idx] = { ...geofence[idx], ...newNotification };
        else geofence.unshift(newNotification);
        geofence = sortNotificationsByTimestampDesc(geofence).slice(0, NOTIFICATION_MAX_GEOFENCE_IN_UI);
      }

      offline = sortNotificationsByTimestampDesc(offline).slice(0, NOTIFICATION_MAX_OFFLINE_IN_UI);
      state.notifications = [...geofence, ...offline].slice(0, NOTIFICATION_MAX_IN_MEMORY);
      state.unreadCount = state.notifications.filter((n) => !n.isRead).length;
    },

    markNotificationAsRead: (state, action) => {
      const id = action.payload;
      const notif = state.notifications.find(n => n.id === id);
      if (notif && !notif.isRead) {
        notif.isRead = true;
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },

    removeNotification: (state, action) => {
      const id = action.payload;
      const notif = state.notifications.find(n => n.id === id);
      if (notif && !notif.isRead)
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      state.notifications = state.notifications.filter(n => n.id !== id);
    },

    clearError: (state) => { state.error = null; },
    setLoading: (state, action) => { state.loading = action.payload; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadUserNotificationState.fulfilled, (state, action) => {
        state.userReadIds = action.payload.readIds;
        state.userDismissedIds = action.payload.dismissedIds;
      })
      .addCase(markNotificationsAsRead.pending, (state) => {
        state.loading = true;
      })
      .addCase(markNotificationsAsRead.fulfilled, (state) => {
        state.loading = false;
        state.notifications = state.notifications.map(n => ({ ...n, isRead: true }));
        state.unreadCount = 0;
      })
      .addCase(markNotificationsAsRead.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(clearAllNotifications.pending, (state) => {
        state.loading = true;
      })
      .addCase(clearAllNotifications.fulfilled, (state) => {
        state.loading = false;
        state.notifications = [];
        state.unreadCount = 0;
      })
      .addCase(clearAllNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  updateNotifications,
  setUserNotificationState,
  addNotification,
  markNotificationAsRead,
  removeNotification,
  clearError,
  setLoading,
} = notificationsSlice.actions;

export default notificationsSlice.reducer;
